import {useNavigate, useParams} from "react-router-dom";
import {Alert, Autocomplete, Box, Divider, Stack} from "@mui/material";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {Rate} from "crqlar-react-common/dist/models/rate/rate.model";
import Property from "crqlar-react-common/dist/components/Property";
import {RateService} from "crqlar-react-common/dist/services/rate/rate.service";
import LocalizedTextField from "crqlar-react-common/dist/components/LocalizedTextField";
import AmountField from "crqlar-react-common/dist/components/AmountField";
import {useAllRates} from "crqlar-react-common/dist/utils/hooks/rates/useRates";
import RepeatableEntityConfig from "crqlar-react-common/dist/components/RepeatableEntityConfig";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import TaxRatePicker from "crqlar-react-common/dist/components/TaxRatePicker";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import {isRequiredTextMissing} from "src/utils/requiredField.util";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";

const RateDetails = () => {

    //region State

    useTrackScreenVisit('system rate details')

    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()

    const rateData = useEntity({
        get: async () => {
            if (id && id !== 'new') {
                return RateService.getRateById(id)
            } else {
                return {}
            }
        },
        create: RateService.createRate,
        update: RateService.updateRate,
        delete: (id) => RateService.deleteRate({id: [id]}),
        onDeleted: () => navigate('/system/rates', {replace: true}),
        onSaved: (rate, wasCreateAction) => {
            if (wasCreateAction) {
                navigate(`/system/rates/${rate.id}`, {replace: true})
            }
        },
        isValid: (rate) => {
            return !!rate.name?.trim()
        },
        cleanupBeforeSave: (rate) => {
            return {
                ...rate,
                name: rate.name?.trim()
            }
        }
    }, id)

    const rate = rateData.item
    const setRate = rateData.setItem
    const nameMissing = isRequiredTextMissing(rateData, rate.name)
    const isDerived = !!rate.parentRateId
    const allRates = useAllRates().filter(r => r.id !== rate.id && !r.parentRateId)

    //endregion Sate

    //region Handlers

    const updateField = (field: keyof Rate, value: any) => {
        setRate({...rate, [field]: value})
    }

    //endregion Handlers

    //region UI

    const headerSection = (
        <SettingsEntityHeader
            title={rate.name}
            data={rateData}
            back={() => navigate('/system/rates')}
        />
    )

    const basicInfoSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Basic info')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Name')}
                    tooltip={t('Display label for this rate in service pricing and selection screens.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={rate.name ?? ''}
                        onChange={(e) => updateField('name', e.target.value)}
                        fullWidth
                        required
                        error={nameMissing}
                    />
                </Property>

                <Property
                    title={t('Description')}
                    tooltip={t('Internal description to help staff distinguish similar rates.')}
                >
                    <TextField
                        id={'description'}
                        size={'small'}
                        value={rate.description ?? ''}
                        onChange={(e) => updateField('description', e.target.value)}
                        fullWidth
                        required
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const priceSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Price')}
            </Typography>

            <Stack gap={1}>
                <Alert
                    severity={'info'}
                    sx={{alignItems: 'center'}}
                >
                    {t('Set parent rate to do relative price adjustments')}
                </Alert>

                <Property
                    title={t('Parent rate (for derived rates)')}
                    tooltip={t('Choose a base rate to create a derived rate with a fixed or percentage price change.')}
                >
                    <Autocomplete
                        disablePortal
                        id={'parent-rate'}
                        options={allRates}
                        fullWidth
                        getOptionLabel={(option: Rate) => option.name || ''}
                        value={allRates.find(r => r.id === rate.parentRateId) || null}
                        renderInput={(params) => <TextField {...params} size={'small'}/>}
                        onChange={(e, value) => {
                            if (value) {
                                setRate({
                                    ...rate,
                                    parentRateId: value.id || undefined,
                                    price: undefined,
                                    rateChange: undefined
                                })
                            } else {
                                setRate({
                                    ...rate,
                                    parentRateId: undefined,
                                    price: undefined,
                                    rateChange: undefined
                                })
                            }
                        }}
                    />
                </Property>

                {
                    !isDerived &&
                    <Property
                        title={t('Price')}
                        tooltip={t('Base amount charged when this rate is used directly.')}
                    >
                        <AmountField
                            id={'price'}
                            value={rate.price}
                            onChange={(e) => updateField('price', e)}
                        />
                    </Property>
                }

                {
                    isDerived &&
                    <Property
                        title={t('Price change')}
                        tooltip={t('Adjustment applied to the parent rate; use either an absolute amount or a percentage.')}
                    >
                        <Stack
                            direction={{xs: 'column', sm: 'row'}}
                            alignItems={'center'}
                            gap={1}
                            width={'100%'}
                        >
                            <TextField
                                id={'change-absolute-cents'}
                                label={t('Absolute change')}
                                size={'small'}
                                value={rate.rateChange?.totalAmountChangeCent !== undefined ? (rate.rateChange.totalAmountChangeCent / 100) : ''}
                                onChange={(e) => {
                                    let floatValue: number | undefined = parseFloat(e.target.value)
                                    if (isNaN(floatValue)) {
                                        floatValue = undefined
                                    }

                                    const amountCents = floatValue !== undefined ? Math.round(floatValue * 100) : undefined

                                    setRate({
                                        ...rate,
                                        rateChange: {
                                            ...rate.rateChange,
                                            totalAmountChangeCent: amountCents,
                                            totalPercentageChange: undefined
                                        }
                                    })
                                }}
                                fullWidth
                                required
                                type={'number'}
                                sx={{flex: 1}}
                            />

                            <TextField
                                id={'change-relative-percent'}
                                label={t('Relative change (%)')}
                                size={'small'}
                                value={rate.rateChange?.totalPercentageChange ?? ''}
                                onChange={(e) => {
                                    let floatValue: number | undefined = parseFloat(e.target.value)
                                    if (isNaN(floatValue)) {
                                        floatValue = undefined
                                    }

                                    setRate({
                                        ...rate,
                                        rateChange: {
                                            ...rate.rateChange,
                                            totalAmountChangeCent: undefined,
                                            totalPercentageChange: floatValue
                                        }
                                    })
                                }}
                                fullWidth
                                required
                                type={'number'}
                                sx={{flex: 1}}
                            />
                        </Stack>
                    </Property>
                }

                <Property
                    title={t("Tax rate")}
                    tooltip={t('Tax rate applied to prices that use this rate.')}
                >
                    <TaxRatePicker
                        id={'tax-rate'}
                        value={rate.taxRateId}
                        onChange={e => updateField('taxRateId', e)}
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const timeLimitSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Time limit / Rate validity')}
            </Typography>

            <RepeatableEntityConfig setEntity={r => r && setRate(r)} entity={rate} showDividers={false}/>
        </Stack>
    )

    const translationsSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Translations')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Name translations')}
                    tooltip={t('Localized rate names shown in guest-facing content and translated interfaces.')}
                >
                    <LocalizedTextField
                        id={'nameLocalized'}
                        size={'small'}
                        value={rate.nameLocalized}
                        onChange={(e) => updateField('nameLocalized', e)}
                        fullWidth
                        required
                    />
                </Property>

                <Property
                    title={t('Description translations')}
                    tooltip={t('Localized rate descriptions shown where this rate is presented to guests.')}
                >
                    <LocalizedTextField
                        id={'descriptionLocalized'}
                        size={'small'}
                        value={rate.descriptionLocalized}
                        onChange={(e) => updateField('descriptionLocalized', e)}
                        fullWidth
                        required
                        multiline
                        rows={3}
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete rate')}
                description={t('This permanently deletes the rate. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={rateData.saving}
                onDelete={() => rateData.removeNow()}
            />
            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {rate.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    //endregion UI

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {basicInfoSection}
                <Divider/>
                {priceSection}
                <Divider/>
                {timeLimitSection}
                <Divider/>
                {translationsSection}

                {
                    !!rate.id &&
                    <>
                        <Divider/>
                        {dangerZoneSection}
                    </>
                }
            </Stack>
        </Box>
    );
}

export default RateDetails;
